<script setup lang="ts">
import {computed, type HTMLAttributes} from 'vue'
import {RadioGroupIndicator, RadioGroupItem, type RadioGroupItemProps, useForwardPropsEmits,} from 'radix-vue'
import {cn} from '@/shared/lib/utils'
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {IconDefinition} from "@fortawesome/fontawesome-svg-core";

interface Props extends RadioGroupItemProps {
  label?: String
  description?: string
  selectable?: boolean
  disabled?: boolean
  icon?: IconDefinition
  iconClass?: HTMLAttributes['class']
  class?: HTMLAttributes['class']
}

const props = withDefaults(defineProps<Props>(), {
  selectable: true,
  disabled: false
})

const delegatedProps = computed(() => {
  const { class: _, ...delegated } = props

  return delegated
})

const useConditionalEmits = () => (event: string, ...args: unknown[]) => {
  //@ts-ignore
  if (!props.disabled) emits(event, ...args);
};
const forwardedProps = useForwardPropsEmits(delegatedProps, useConditionalEmits())

</script>

<template>
  <RadioGroupItem
    v-bind="forwardedProps"
    :disabled="disabled"
    :class="
      cn(
        'relative flex cursor-pointer bg-white p-4 shadow-sm focus:outline-none border border-slate-200 rounded-md z-0 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-50',
        props.class,
      )
    "
  >
    <div class="w-full h-full" v-if="$slots.default">
      <slot />
    </div>
    <template v-else>
      <div class="flex flex-1 justify-start h-full" :class="{'items-start' : label && description}">
        <div v-if="icon" class="flex items-center mr-3" :class="{'flex flex-1 items-center justify-center h-full' : !label && !description}">
          <FontAwesomeIcon :icon="icon" :class="iconClass" />
        </div>
        <span class="flex flex-col justify-center">
          <span v-if="label" class="text-left block text-sm font-medium text-gray-900 pr-3">{{ label }}</span>
          <span v-if="description" class="text-left mt-1  text-sm text-slate-500" v-html="description" />
        </span>
      </div>
    </template>
    <RadioGroupIndicator v-if="selectable"
      class="flex items-center justify-center"
    >
      <svg class="h-5 w-5 text-green-500 absolute right-2 top-2" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" />
      </svg>
      <span class="pointer-events-none absolute -inset-px rounded-lg border-2 border-primary" aria-hidden="true"></span>
    </RadioGroupIndicator>
  </RadioGroupItem>
</template>


